import moment from "moment";

import { LinkPatientPopupOptions } from "@/Common/Components/Labs/LinkPatientToLabPopup";
import { AdditionalSignOffInformation } from "@/Common/Components/Modals/LinkingPopup/LinkingPopup";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { Guid } from "@/Common/Models/Guid";
import { OntarioPatientEnrollmentModel } from "@/Common/Models/Patient/PatientEnrollment/Ontario/OntarioPatientEnrollmentModel";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { LabTabViews, RedirectTo } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { LabMessage } from "@/Labs/Models/LabMessage";
import { ISideNoteModel } from "@/Labs/Models/UI/ISideNoteModel";
import { LabsStore } from "@/Labs/Stores/LabsStore";

class LabViewHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.LabsUI.ToggleAdditionalInfo, this.toggleAdditionalInfo);
        this.RegisterOperation(LocalOperations.LabsUI.ToggleAbnormalAndCriticalInfo, this.toggleAbnormalAndCriticalInfo);
        this.RegisterOperation(LocalOperations.LabsUI.ToggleResults, this.toggleResults);
        this.RegisterOperation(LocalOperations.LabsUI.ToggleHoldFileCheckBox, this.toggleHoldFileCheckbox);
        this.RegisterOperation(LocalOperations.LabsUI.NextLab, this.nextLab);
        this.RegisterOperation(LocalOperations.LabsUI.PreviousLab, this.previousLab);
        this.RegisterOperation(LocalOperations.LabsUI.HoldLab, this.holdLab);
        this.RegisterOperation(LocalOperations.LabsUI.FileLab, this.fileLab);
        this.RegisterOperation(LocalOperations.LabsUI.PutOnHold_SaveAndOpenEncounter, this.putOnHoldSaveAndOpenEncounter);
        this.RegisterOperation(LocalOperations.LabsUI.PutOnHold_ApprouveEncounter, this.putOnHoldApprouveEncounter);
        this.RegisterOperation(LocalOperations.LabsUI.File_SaveAndOpenEncounter, this.fileSaveAndOpenEncounter);
        this.RegisterOperation(LocalOperations.LabsUI.File_ApprouveEncounter, this.fileApprouveEncounter);
        this.RegisterOperation(LocalOperations.LabsUI.ExpandLeftMenu, this.expandLeftMenu);
        this.RegisterOperation(LocalOperations.LabsUI.CollapseLeftMenu, this.collapseLeftMenu);
        this.RegisterOperation(LocalOperations.LabsUI.ToggleLinkPatientPopup, this.toggleLinkPatientPopup);
        this.RegisterOperation(LocalOperations.LabsUI.SelectPatientForLinking, this.selectPatientForLinking);
        this.RegisterOperation(LocalOperations.LabsUI.LinkSelectedPatient, this.linkSelectedPatient);
        this.RegisterOperation(LocalOperations.LabsUI.OpenAttachment, this.openAttachment);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_SpecialityToggle, this.siteNoteSpecialityToggle);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_RaisonChange, this.siteNoteReasonChange);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_LaboratoireChange, this.siteNoteLaboratoireChange);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_ImagerieChange, this.siteNoteImageChange);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_RequeteChange, this.siteNoteRequestChange);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_UrgentRequeteChange, this.siteNoteUrgentRequestChange);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_ConsultationChange, this.siteNoteConsultationChange);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_UrgentConsultationChange, this.siteNoteUrgentConsultationChange);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_ConsultationSpecialityChange, this.siteNoteConsultationSpecialityChange);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_ConsultationDestinataireSelect, this.siteNoteConsultationDestiantaireSelect);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_ConsultationDestinataireClear, this.siteNoteConsultationDestinataireClear);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_ConsultationDestiantaireTextChanged, this.siteNoteConsultationDestiantaireTextChanged);
        this.RegisterOperation(LocalOperations.LabsUI.SiteNote_FollowUpChange, this.siteNoteFollowUpChange);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.onContainerClick, false);
        this.RegisterOperation(LocalOperations.LabsUI.ShowClickableText, this.toggleClickableTextVisibility);
        this.RegisterOperation(LocalOperations.LabsUI.HideClickableText, this.hideClickableText);
        this.RegisterOperation(LocalOperations.LabsUI.PortalStatusClicked, this.onPortalStatusClicked);
        this.RegisterOperation(LocalOperations.LabsUI.PortalStatusCancel, this.onPortalStatusCanceled);
        this.RegisterOperation(LocalOperations.LabsUI.PortalStatusSend, this.onPortalStatusSend);
        this.RegisterOperation(LocalOperations.LabsUI.CloseSendToPortalPopup, this.onCloseSendToPortalPopup);
        this.RegisterOperation(LocalOperations.LabsUI.PortalStatusRevoked, this.onRevokePatientLab);
        this.RegisterOperation(LocalOperations.LabsUI.OnPortalLabItemChecked, this.onPortalLabItemChecked);
        this.RegisterOperation(LocalOperations.LabsUI.LoadLabTaskStatus, this.loadLabTaskStatus);
        this.RegisterOperation(LocalOperations.LabsUI.ShowAttachedCommunicationsPopup, this.showAttachedCommunicationsPopup);
        this.RegisterOperation(LocalOperations.LabsUI.HideAttachedCommunicationsPopup, this.hideAttachedCommunicationsPopup);
        this.RegisterOperation(LocalOperations.LabsUI.RedirectAndCloseAttachedCommunicationPopup, this.redirectAndCloseAttachedCommunicationPopup);
        this.RegisterOperation(LocalOperations.LabsUI.ShowExternalCommunicationPopup, this.handleShowExternalCommunication);
        this.RegisterOperation(LocalOperations.LabsUI.ToggleCreatePatientPopup, this.toggleCreatePatientPopup);
        this.RegisterOperation(LocalOperations.LabsUI.CreateNewPatientAndLinkResult, this.createNewpatientAndLinkResult);
        this.RegisterOperation(LocalOperations.LabsUI.SetOntarioEnrollmentPatientBanner, this.setOntarioEnrollmentPatientBanner);
        this.RegisterOperation(LocalOperations.LabsUI.FileManualLab, this.fileManualLab);
    }

    private toggleHoldFileCheckbox(request: LocalEvent, store: LabsStore): void {
        store.LabModel = store.LabModel.set("OnHoldCheckBox", !store.LabModel.get("OnHoldCheckBox"));
    }

    private onContainerClick(request: LocalEvent, store: LabsStore): void {
        store.LabModel = store.LabModel
            .set("ReasonClickableTextVisible", false)
            .set("LabClickableTextVisible", false)
            .set("ImagingClickableTextVisible", false)
            .set("RequestClickableTextVisible", false)
            .set("ConsultClickableTextVisible", false)
            .set("FollowUpPlanClickableTextVisible", false);

        store.CloseActionNotePopup(store);
        store.EmitChangeToListeners();
    }

    private siteNoteReasonChange(request: LocalEvent, store: LabsStore): void {
        store.SideNoteModel = store.SideNoteModel.set("Raison", request.Get<string>("newText"));
    }

    private siteNoteLaboratoireChange(request: LocalEvent, store: LabsStore): void {
        store.SideNoteModel = store.SideNoteModel.set("Laboratoire", request.Get<string>("newText"));
    }

    private siteNoteImageChange(request: LocalEvent, store: LabsStore): void {
        store.SideNoteModel = store.SideNoteModel.set("Imagerie", request.Get<string>("newText"));
    }

    private siteNoteRequestChange(request: LocalEvent, store: LabsStore): void {
        store.SideNoteModel = store.SideNoteModel.set("Requete", request.Get<string>("newText"));
    }

    private siteNoteUrgentRequestChange(request: LocalEvent, store: LabsStore): void {
        store.SideNoteModel = store.SideNoteModel.set("UrgentRequete", request.Get<boolean>("checked"));
    }

    private siteNoteConsultationChange(request: LocalEvent, store: LabsStore): void {
        store.SideNoteModel = store.SideNoteModel.set("Consultation", request.Get<string>("newText"));
    }

    private siteNoteUrgentConsultationChange(request: LocalEvent, store: LabsStore): void {
        store.SideNoteModel = store.SideNoteModel.set("UrgentConsultation", request.Get<boolean>("checked"));
    }

    private siteNoteConsultationSpecialityChange(request: LocalEvent, store: LabsStore): void {
        store.SideNoteModel = store.SideNoteModel.set("ConsultationSpeciality", request.Get<string>("value"));
    }

    private siteNoteConsultationDestiantaireSelect(request: LocalEvent, store: LabsStore): void {
        const selectedDoctor = request.Get<PersonDropdownItem>("value");
        if (selectedDoctor != null)
            store.SideNoteModel = store.SideNoteModel.set("ConsultationDestinataire", Immutable.List([selectedDoctor.Item]));
        else
            store.SideNoteModel = store.SideNoteModel.delete("ConsultationDestinataire");
    }

    private siteNoteConsultationDestinataireClear(request: LocalEvent, store: LabsStore): void {
        store.SideNoteModel = store.SideNoteModel
            .delete("ConsultationDestinataire")
            .set("ConsultationDestinataireFreeText", "");
    }

    private siteNoteConsultationDestiantaireTextChanged(request: LocalEvent, store: LabsStore): void {
        store.SideNoteModel = store.SideNoteModel.set("ConsultationDestinataireFreeText", request.Get<string>("value"));
    }

    private siteNoteFollowUpChange(request: LocalEvent, store: LabsStore): void {
        store.SideNoteModel = store.SideNoteModel.set("FollowUp", request.Get<string>("newText"));
    }

    private openAttachment(request: LocalEvent, store: LabsStore): void {
        const encapsulatedDataId = request.Get<string>("id");
        store.LabActions.GetEncapsulatedData(encapsulatedDataId);
    }

    private selectPatientForLinking(request: LocalEvent, store: LabsStore): void {
        store.LabModel = store.LabModel.set("SelectedPersonForLinkingLab", request.Get<Person>("person"));
    }

    private linkSelectedPatient(request: LocalEvent, store: LabsStore): void {
        const patientToLink = store.LabModel.get("SelectedPersonForLinkingLab");
        const labFillerID = store.LabModel.get("LabMessage").Model.LabFillerID;

        store.LabModel = store.LabModel
            .set("ShowLinkPatientPopup", false)
            .set("LinkPatientPopupOptions", null);
        store.LabActions.LinkPatient(labFillerID, patientToLink.ContactId);
    }

    private toggleLinkPatientPopup(request: LocalEvent, store: LabsStore): void {
        const state = store.LabModel.get("ShowLinkPatientPopup");
        store.LabModel = store.LabModel
            .set("ShowLinkPatientPopup", !state)
            .set("LinkPatientPopupOptions", null);
    }

    private nextLab(request: LocalEvent, store: LabsStore): void {
        this.rawNextLab(store);
    }
    private rawNextLab(store: LabsStore): void {
        if (store.LabFillerIDsViewedNaviguationPosition != -1) {
            const tableData = store.LabsInboxModel.get("LabsTableData");
            const nextIndex = store.LabFillerIDsViewedNaviguationPosition + 1;

            if (nextIndex < tableData.count()) {
                // Next Lab is available, just open it.
                const nextlab = tableData.get(nextIndex).Model.Model;
                store.LabFillerIDsViewedNaviguationPosition = store.LabFillerIDsViewedNaviguationPosition + 1;

                store.LabActions.GetLab(nextlab.LabFillerID, SubServerOperations.Labs.GetLab_Default, nextlab.IsManualLabEntry);
            }
            else {
                store.LabsInboxModel = store.LabsInboxModel.set("CurrentPage", 1);
                store.Model = store.Model.set("CurrentView", LabTabViews.Inbox);
                store.LabActions.RefreshLabs();
            }
        }
    }

    private previousLab(): void {
        if (labsStore.LabFillerIDsViewedNaviguationPosition > 0) {
            labsStore.LabFillerIDsViewedNaviguationPosition = labsStore.LabFillerIDsViewedNaviguationPosition - 1;
            const lab = labsStore.GetLabByTablePosition(labsStore.LabFillerIDsViewedNaviguationPosition);

            labsStore.LabActions.GetLab(lab.LabFillerID, SubServerOperations.Labs.GetLab_Default, lab.IsManualLabEntry);
        }
    }

    private holdLab(request: LocalEvent, store: LabsStore): void {
        let labFillerId = request.Get<string>("id");

        // Optional Encounter Actions Management
        let openEncounterId = request.Get<string>("EncounterId"); // Open Encounter
        let openEncounterFromPatientContext = request.Get<boolean>("OpenEncounterFromPatientFileContext"); // Is patient already open
        // --------------------------------------

        this.rawHoldLab(store, labFillerId, openEncounterId, openEncounterFromPatientContext);
    }

    private rawHoldLab(store: LabsStore, labFillerId: string, openEncounterId: string = null, openEncounterFromPatientContext: boolean = false): void {
        store.LabModel = store.LabModel.set("IsSaving", true);
        store.LabActions.HoldLabAction([labFillerId], false);

        // Follow up action
        if (openEncounterId != null) {
            if (openEncounterFromPatientContext == true)
                this.refreshSummaryAndOpenEncounter(openEncounterId, store, true);
            else
                this.openPatient(openEncounterId, store, true);
        }
        else
            this.rawNextLab(store);
    }

    private fileLab(request: LocalEvent, store: LabsStore): void {
        let labFillerId = request.Get<string>("id");

        // Optional Encounter Actions Management
        let openEncounterId = request.Get<string>("EncounterId"); // Open Encounter
        let openEncounterFromPatientContext = request.Get<boolean>("OpenEncounterFromPatientFileContext"); // Is patient already open
        // --------------------------------------

        let model = store.LabModel.get("MylePatient");
        if (!sessionStore.hidePatientLinkUntilLogoff && model == null) {
            store.LabModel = store.LabModel
                .set("ShowLinkPatientPopup", true)
                .set("LinkPatientPopupOptions", new LinkPatientPopupOptions({
                    notLinkedWarningMessage: cacheStore.GetString("LabNotLinkedToAPatientWarningMessageLabel"),
                    ignoreNotLinkedWarningButtonLabel: cacheStore.GetString("ContinueWithoutLinking"),
                    ignoreNotLinkedWarningButtonCallback: () => {
                        store.LabModel = store.LabModel
                            .set("ShowLinkPatientPopup", false)
                            .set("LinkPatientPopupOptions", null);
                        this.rawFileLab(store, labFillerId, openEncounterId, openEncounterFromPatientContext);
                    },
                    saveButtonLabel: cacheStore.GetString("LinkAndContinue")
                }));
            return;
        }

        this.rawFileLab(store, labFillerId, openEncounterId, openEncounterFromPatientContext);
    }
    private rawFileLab(store: LabsStore, labFillerId: string, openEncounterId: string = null, openEncounterFromPatientContext: boolean = false): void {
        store.LabModel = store.LabModel.set("IsSaving", true);
        store.LabActions.FileLabAction([labFillerId], false);

        // Follow up action
        if (openEncounterId != null) {
            if (openEncounterFromPatientContext == true)
                this.refreshSummaryAndOpenEncounter(openEncounterId, store, true);
            else
                this.openPatient(openEncounterId, store, true);
        }
        else
            this.rawNextLab(store);
    }

    public openPatient(EncounterId: string, store: LabsStore, isEncounterEdit: boolean = false): void {
        store.Model = store.Model.set("CurrentView", LabTabViews.Inbox);

        const currentPatient = labsStore.LabModel.get("MylePatient");
        if (currentPatient != null && currentPatient.IndividualResponse != null) {
            window.setTimeout(
                () => {
                    var openPatientOperation = new LocalEvent(LocalOperations.Misc.RedirectToTab);
                    openPatientOperation.AddItem("value", currentPatient.IndividualResponse.Contact_id);
                    openPatientOperation.AddItem("target", RedirectTo.Patient);
                    openPatientOperation.AddItem("currentLocation", RouterHistory.location);
                    openPatientOperation.AddItem("OpenEncounterId", EncounterId);
                    openPatientOperation.AddItem("encounterEdit", isEncounterEdit);
                    LocalEventDispatcher.dispatch(openPatientOperation);
                },
                50
            );
        }
    }

    public refreshSummaryAndOpenEncounter(EncounterId: string, store: LabsStore, isEncounterEdit: boolean = false): void {
        store.Model = store.Model.set("CurrentView", LabTabViews.Inbox);
        const currentPatient = labsStore.LabModel.get("MylePatient");

        if (currentPatient != null && currentPatient.IndividualResponse != null) {
            window.setTimeout(
                () => {
                    var openPatientOperation = new LocalEvent(LocalOperations.Misc.ReloadPatientSummaryAndGoToEncounter);

                    openPatientOperation.AddItem("encounterId", EncounterId);
                    openPatientOperation.AddItem("encounterEdit", isEncounterEdit);
                    LocalEventDispatcher.dispatch(openPatientOperation);
                },
                50
            );
        }
    }

    private isSideNoteEmpty(note: ISideNoteModel): boolean {
        if (note === undefined || note === null)
            return true;

        return (
            areAllNullOrWhiteSpace([
                note.get("Raison"),
                note.get("Laboratoire"),
                note.get("Imagerie"),
                note.get("Requete"),
                note.get("Consultation"),
                note.get("ConsultationSpeciality"),
                note.get("ConsultationDestinataireFreeText"),
                note.get("FollowUp")
            ])
            && isImmutableListNullOrUndefinedOrEmpty(note.get("ConsultationDestinataire"))
            && !note.get("UrgentRequete")
            && !note.get("UrgentConsultation")
        );
    }

    private putOnHoldSaveAndOpenEncounter(request: LocalEvent, store: LabsStore): void {
        store.LabModel = store.LabModel.set("IsSaving", true);
        store.saveEncounterLabFillerId = store.LabModel.get("LabMessage").Model.LabFillerID;
        // check if we are in patient file context, or general results context
        const isInPatientContext = !IsNullOrWhiteSpace(store.Model.get("PatientContactId"));
        if (!isInPatientContext)
            store.LabActions.SaveEncounter(store.LabModel, store.SideNoteModel, false, SubServerOperations.Labs.LabEncounterHold);
        else
            store.LabActions.SaveEncounter(store.LabModel, store.SideNoteModel, false, SubServerOperations.Labs.LabEncounterHoldFromPatientFile);
    }

    private putOnHoldApprouveEncounter(request: LocalEvent, store: LabsStore): void {
        const additionalSignOffInformation = request.Get<AdditionalSignOffInformation>("additionalSignOffInformation");

        let isEmptyNote = this.isSideNoteEmpty(store.SideNoteModel);
        if (isEmptyNote) {
            this.rawHoldLab(store, store.LabModel.get("LabMessage").Model.LabFillerID);
            return;
        }

        store.LabModel = store.LabModel.set("IsSaving", true);
        store.saveEncounterLabFillerId = store.LabModel.get("LabMessage").Model.LabFillerID;
        // check if we are in patient file context, or general results context
        const isInPatientContext = !IsNullOrWhiteSpace(store.Model.get("PatientContactId"));

        store.LabActions.SaveEncounter(
            store.LabModel,
            store.SideNoteModel,
            true,
            isInPatientContext
                ? SubServerOperations.Labs.LabEncounterHoldFromPatientFile
                : SubServerOperations.Labs.LabEncounterHold,
            additionalSignOffInformation
        );
    }

    private fileSaveAndOpenEncounter(request: LocalEvent, store: LabsStore): void {
        store.LabModel = store.LabModel.set("IsSaving", true);
        store.saveEncounterLabFillerId = store.LabModel.get("LabMessage").Model.LabFillerID;

        // check if we are in patient file context, or general results context
        const isInPatientContext = !IsNullOrWhiteSpace(store.Model.get("PatientContactId"));
        if (!isInPatientContext)
            store.LabActions.SaveEncounter(store.LabModel, store.SideNoteModel, false, SubServerOperations.Labs.LabEncounterFile);
        else
            store.LabActions.SaveEncounter(store.LabModel, store.SideNoteModel, false, SubServerOperations.Labs.LabEncounterFileFromPatientFile);
    }

    private fileApprouveEncounter(request: LocalEvent, store: LabsStore): void {
        const additionalSignOffInformation = request.Get<AdditionalSignOffInformation>("additionalSignOffInformation");

        const isEmptyNote = this.isSideNoteEmpty(store.SideNoteModel);
        if (isEmptyNote) {
            this.rawFileLab(store, store.LabModel.get("LabMessage").Model.LabFillerID);
            return;
        }

        store.LabModel = store.LabModel.set("IsSaving", true);
        store.saveEncounterLabFillerId = store.LabModel.get("LabMessage").Model.LabFillerID;
        store.SideNoteModel = store.SideNoteModel.set("AppointmentId", additionalSignOffInformation.appointmentId);

        // check if we are in patient file context, or general results context
        const isInPatientContext = !IsNullOrWhiteSpace(store.Model.get("PatientContactId"));
        store.LabActions.SaveEncounter(
            store.LabModel,
            store.SideNoteModel,
            true,
            isInPatientContext
                ? SubServerOperations.Labs.LabEncounterFileFromPatientFile
                : SubServerOperations.Labs.LabEncounterFile,
            additionalSignOffInformation
        );
    }

    private expandLeftMenu(request: LocalEvent, store: LabsStore): void {
        store.LabModel = store.LabModel.set("IsNoteExpanded", true);
    }

    private collapseLeftMenu(request: LocalEvent, store: LabsStore): void {
        store.LabModel = store.LabModel.set("IsNoteExpanded", false);
    }

    private siteNoteSpecialityToggle(): void {
        var currentSpeciality = labsStore.Model.get("CurrentSpeciality");

        var Specialities = sessionStore.getCurrentEmployee().getServiceSpecializations();
        var position = Specialities.indexOf(currentSpeciality);
        position += 1;

        if (position > Specialities.length - 1)
            position = 0;

        labsStore.Model = labsStore.Model.set("CurrentSpeciality", Specialities[position]);
    }

    private toggleAdditionalInfo(request: LocalEvent, store: LabsStore): void {
        var state = store.LabModel.get("ShowAdditionalDetails");
        store.LabModel = store.LabModel.set("ShowAdditionalDetails", !state);
    }

    private toggleAbnormalAndCriticalInfo(request: LocalEvent, store: LabsStore): void {
        var state = store.LabModel.get("ShowAbnormalAndCriticalObservations");
        store.LabModel = store.LabModel.set("ShowAbnormalAndCriticalObservations", !state);
    }

    private toggleResults(request: LocalEvent, store: LabsStore): void {
        var state = store.LabModel.get("ShowResults");
        store.LabModel = store.LabModel.set("ShowResults", !state);
    }

    private hideClickableText(): void {
        let labModel = labsStore.LabModel;
        labModel = labModel.set("ReasonClickableTextVisible", false);
        labModel = labModel.set("LabClickableTextVisible", false);
        labModel = labModel.set("ImagingClickableTextVisible", false);
        labModel = labModel.set("RequestClickableTextVisible", false);
        labModel = labModel.set("ConsultClickableTextVisible", false);
        labModel = labModel.set("FollowUpPlanClickableTextVisible", false);
        labsStore.LabModel = labModel;
    }

    private toggleClickableTextVisibility(request: LocalEvent): void {
        var value = request.Get<string>("value");

        this.hideClickableText();

        let labModel = labsStore.LabModel;

        switch (value) {
            case "reason": labModel = labModel.set("ReasonClickableTextVisible", true); break;
            case "lab": labModel = labModel.set("LabClickableTextVisible", true); break;
            case "imaging": labModel = labModel.set("ImagingClickableTextVisible", true); break;
            case "request": labModel = labModel.set("RequestClickableTextVisible", true); break;
            case "consult": labModel = labModel.set("ConsultClickableTextVisible", true); break;
            case "followupplan": labModel = labModel.set("FollowUpPlanClickableTextVisible", true); break;
        }
        labsStore.LabModel = labModel;

        sessionStore.hideGlobalOverlay();
    }

    private onPortalStatusClicked(request: LocalEvent, store: LabsStore): void {
        let lab = request.Get<LabMessage>("lab");
        if (lab != null && lab.Model != null) {
            // get lab actions and notes from backend
            store.LabActions.GetLab(lab.Model.LabFillerID, SubServerOperations.Labs.GetLab_FromPortalSyncPopupInInbox);

            const status = request.Get<PortalRegistrationState>("registrationStatus");
            const patientContactId = request.Get<string>("patientContactId");
            let portalModel = store.LabsInboxModel.get("PortalLinkedItemModel");
            portalModel = portalModel.set("RegistrationStatus", status);
            portalModel = portalModel.set("PatientContactId", patientContactId);
            store.LabsInboxModel = store.LabsInboxModel.set("PortalLinkedItemModel", portalModel);
        }
    }

    private onPortalStatusCanceled(request: LocalEvent, store: LabsStore): void {
        let docSyncModel = store.LabsInboxModel.get("PortalLinkedItemModel");
        docSyncModel = PatientHelper.clearPortalLinkItemStates(docSyncModel);
        store.LabsInboxModel = store.LabsInboxModel.set("PortalLinkedItemModel", docSyncModel);
        store.LabsInboxModel = store.LabsInboxModel.set("IsSendToPatientPortalPopupOpen", false);
        $("table.myle-table tbody tr").removeClass("active");
        sessionStore.hideGlobalOverlay();
    }

    private onPortalStatusSend(request: LocalEvent, store: LabsStore): void {
        let subject = request.Get<string>("subject");
        let messageText = request.Get<string>("messageText");
        let includeDocument = request.Get<boolean>("includeDocument");
        const isFromPatientLabTab = !IsNullOrWhiteSpace(store.Model.get("PatientContactId"));
        const priority = request.Get<SharedItemPriority>("priority");
        const canPatientReplyWithMessage = request.Get<boolean>("canPatientReplyWithMessage");

        let syncModel = store.LabsInboxModel.get("PortalLinkedItemModel");
        let labmessage = syncModel.get("LinkedItemModel") as LabMessage;

        let portalMessages: SharedItemMessage[] = [];
        if (syncModel != null) {
            portalMessages = PatientHelper.getShareItemMessagesToRevokeOrToSendToPortal(syncModel);
        }

        const hasTextMessage = !IsNullOrWhiteSpace(messageText);

        let patientId = isFromPatientLabTab ? patientStore.getCurrentPatientContactId() : store.LabModel.get("MylePatient").IndividualResponse.Contact_id;

        if (IsNullOrWhiteSpace(patientId)) {
            throw "Error - Can't send to portal without a patientLink... ";
        }

        let sharedItem = syncModel.get("LinkedItemModel");
        let sharedItemId: string = sharedItem.getSharedItemId();

        // if portal status already has a linked item id, it was already synced, so reuse ID
        // else we create a new GUID, cause labs use labfiller as id and we must add a GUID id in LabMetadaPortalLink
        let objectId = syncModel.get("LinkedItemId");

        // ensure adequate values are set for portal message
        portalMessages.forEach(x => {
            x.DateCreated = x.DateCreated != null ? x.DateCreated : moment().toDate();
        });

        // create new message if user has entered text in message textbox
        if (hasTextMessage) {
            portalMessages.push(SharedItemMessage.createNewSharedItemMessage(messageText, priority));
        }

        store.LabActions.SendLabToPortal(
            objectId,
            sharedItemId,
            subject,
            PortalLinkedItemType.Result,
            sessionStore.getCurrentEmployee().ContactId,
            patientId,
            sessionStore.userId,
            portalMessages,
            labmessage.Model,
            includeDocument,
            sharedItem.hasBeenModifiedSinceLastShared(),
            canPatientReplyWithMessage
        );

        syncModel = syncModel.set("IsSending", true);
        store.LabsInboxModel = store.LabsInboxModel.set("PortalLinkedItemModel", syncModel);
    }

    private onCloseSendToPortalPopup(request: LocalEvent, store: LabsStore): void {
        store.LabsInboxModel = store.LabsInboxModel.set("IsSendToPatientPortalPopupOpen", false);
    }

    private onRevokePatientLab(request: LocalEvent, store: LabsStore): void {
        let messageIdsToRevoke: number[] = [];
        var model = store.LabsInboxModel.get("PortalLinkedItemModel");

        let objectId: string = !IsNullOrWhiteSpace(model.get("LinkedItemId")) ? model.get("LinkedItemId") : "";
        if (model.get("LinkedItemMessages") != null) {
            let allPortalMessages = model.get("LinkedItemMessages");

            // revoke only pending messages
            allPortalMessages.map(x => {
                if (x.Status === PortalSyncStatus.Pending ||
                    x.Status === PortalSyncStatus.Viewed ||
                    x.Status === PortalSyncStatus.Error ||
                    x.Status === PortalSyncStatus.Unknown) {
                    messageIdsToRevoke.push(+x.SharedItemMessageId);
                }
            });
        }

        let sharedItemId: string = model.get("LinkedItemModel").getSharedItemId();

        store.LabActions.RevokeLabSharedItemToPortal(sharedItemId, messageIdsToRevoke, sessionStore.userId, PortalLinkedItemType.Result, objectId, true);
    }

    private onPortalLabItemChecked(request: LocalEvent, store: LabsStore): void {
        var checkedItem = request.Get<SharedItemMessage>("item");

        if (checkedItem != null) {
            var model = store.LabsInboxModel.get("PortalLinkedItemModel");
            model = PatientHelper.handlePortalItemCheck(model, checkedItem);
            store.LabsInboxModel = store.LabsInboxModel.set("PortalLinkedItemModel", model);
        }
    }

    private loadLabTaskStatus(request: LocalEvent, store: LabsStore): void {
        let labFillerId = request.Get<string>("labFillerId");
        store.LabActions.loadAttachedTasks(labFillerId);
    }

    private showAttachedCommunicationsPopup(request: LocalEvent, store: LabsStore): void {
        let eventY = request.Get<number>("eventY");
        let eventX = request.Get<number>("eventX");

        store.LabModel = store.LabModel
            .set("AttachedCommunicationsPopupY", eventY)
            .set("AttachedCommunicationsPopupX", eventX)
            .set("AttachedCommunicationsPopupOpen", true);
    }

    private hideAttachedCommunicationsPopup(request: LocalEvent, store: LabsStore): void {
        store.LabModel = store.LabModel
            .set("AttachedCommunicationsPopupOpen", false);
    }

    private redirectAndCloseAttachedCommunicationPopup(request: LocalEvent, store: LabsStore): void {
        let redirect = request.Get<() => void>("redirect");

        store.LabModel = store.LabModel
            .set("AttachedCommunicationsPopupOpen", false);

        window.setTimeout(() => {
            redirect && redirect();
        }, 200);
    }

    private handleShowExternalCommunication(request: LocalEvent, store: LabsStore): void {
        let showShow = request.Get<boolean>("show");
        store.LabModel = store.LabModel
            .set("ShowExternalCommunicationPopup", showShow);
    }

    private getGenderLookupId(gender: string): string {
        switch (gender?.toUpperCase()) {
            case "F":
            case "FEMALE":
                return cacheStore.FindLookupByLookupCode("FEMALE").LookupID;

            case "M":
            case "MALE":
                return cacheStore.FindLookupByLookupCode("MALE").LookupID;

            default:
                return null;
        }
    }

    private autofillCreatePatientForm(store: LabsStore): void {
        const patient = store.LabModel.get("LabMessage").Model.Patient;
        const firstName = patient.FirstName?.split(/[-\s]/).map(p => p.toTitleCase()).join("-");
        const lastName = patient.LastName?.split(/[-\s]/).map(p => p.toTitleCase()).join("-");

        const genderLookupId = this.getGenderLookupId(patient?.Sex);

        let patientCreationModel = store.LabModel.get("PatientCreationModel")
            .set("FirstName", firstName)
            .set("LastName", lastName)
            .set("DOB", LuxonDateTimeParser.parse(patient?.DateOfBirth)?.toJSDate())
            .set("NAMEdit", patient?.NAM)
            .set("selectedHealthCardRegionLookupId", patient?.NamProvinceCode?.toString())
            .set("GenderLookupId", genderLookupId);

        if (!IsNullOrUndefined(patient?.Phones[1]?.Number)) {
            patientCreationModel = patientCreationModel
                .set("Phone1Edit", patient?.Phones[1]?.Number?.replace(/[\(\)\-]/g, ""))
                .set("Phone1TypeLookupId", cacheStore.getLookupByPhoneType(PhoneType.Phone).LookupID)
                .set("Phone1ContactTypeLookupId", cacheStore.getLookupByContactPointType(patient?.Phones[1]?.IsBusiness ? ContactPointType.Work : ContactPointType.Personal).LookupID);
        }

        if (!IsNullOrUndefined(patient?.Phones[0]?.Number)) {
            patientCreationModel = patientCreationModel
                .set("Phone2Edit", patient?.Phones[0]?.Number.replace(/[\(\)\-]/g, ""))
                .set("Phone2TypeLookupId", cacheStore.getLookupByPhoneType(PhoneType.Phone).LookupID)
                .set("Phone2ContactTypeLookupId", cacheStore.getLookupByContactPointType(patient?.Phones[0]?.IsBusiness ? ContactPointType.Work : ContactPointType.Personal).LookupID);
        }

        if (patient?.Addresses[0]) {
            const countryRegionName = !IsNullOrUndefined(patient?.Addresses[0]?.CustomCountryOrProvince) ? patient?.Addresses[0]?.CustomCountryOrProvince?.split(", ") : [];
            const countryName = countryRegionName[1];
            const regionName = countryRegionName[0];
            const countryLookup = cacheStore.getCountryLookupByCountryNameOrDefaultActiveSite(countryName);
            const regionLookupId = cacheStore.getRegionLookupIdOrDefaultActiveSite(regionName, countryLookup);

            patientCreationModel = patientCreationModel
                .set("CityEdit", patient?.Addresses[0]?.City?.toTitleCase())
                .set("PostalCodeEdit", patient?.Addresses[0]?.ZipCode)
                .set("CivicAddressEdit", patient?.Addresses[0]?.StreetAddress?.toTitleCase())
                .set("SelectedCountryLookupId", countryLookup.LookupID)
                .set("SelectedRegionLookupId", regionLookupId);
        }

        store.LabModel = store.LabModel.set("PatientCreationModel", patientCreationModel);
    }

    private toggleCreatePatientPopup(request: LocalEvent, store: LabsStore): void {
        const popupState = store.LabModel.get("ShowCreatePatientPopup");
        const unLinkedPatient = !store.LabModel.get("MylePatient");

        if (!popupState) {
            store.LabModel = store.LabModel.set("PatientCreationModel", Immutable.fromJS(PatientHelper.getDefaultPatientCreationModel()));
            if (unLinkedPatient)
                this.autofillCreatePatientForm(store);
        }

        store.LabModel = store.LabModel
            .set("ShowCreatePatientPopup", !popupState)
            .set("LinkPatientPopupOptions", null);
    }

    private createNewpatientAndLinkResult(request: LocalEvent, store: LabsStore): void {
        const patient = request.Get<IPatientCreationModel>("patient");
        store.LabActions.createPatient(patient);
    }

    private setOntarioEnrollmentPatientBanner(request: LocalEvent, store: LabsStore): void {
        const enrollmentStatus = request.Get<OntarioPatientEnrollmentModel>("enrollmentStatus");
        let patient = store.LabModel.get("MylePatient");

        if (patient) {
            patient.setOntarioPatientEnrollmentModel(enrollmentStatus);

            store.LabModel = store.LabModel
                .set("MylePatient", patient);
        }
    }

    private fileManualLab(request: LocalEvent, store: LabsStore): void {
        const labFillerId: Guid = request.Get<Guid>("id");
        store.LabActions.FileManualLabResults([labFillerId], false);
        store.LabActions.GetLab(labFillerId, SubServerOperations.Labs.GetLab_Default, true);

        this.nextLab(request, store);
    }
}

export { LabViewHandler };
